/**
 * SubmissionIntelligencePanel — "Research & Check Submission" flow.
 *
 * Single-button UX: the student sees ONE button. Behind the scenes the
 * backend orchestrates Brave (web similarity + factual lookups) and Exa
 * (academic evidence). Results are combined into a single "Submission
 * Intelligence Report" with six sections:
 *    - Grade Prediction + AI Feedback (recap of existing analysis)
 *    - Web Similarity  (Brave)
 *    - Evidence Strength + Suggested Evidence  (Exa)
 *    - Submission Risk  (composite)
 *    - Outdated References
 *    - Actionable Improvements
 *
 * Gating: All Pro + Ultra Pro (backend returns 402 for free users).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Search, Globe, BookOpen, AlertTriangle, Calendar,
  Lightbulb, ExternalLink, ShieldCheck, TrendingUp, X, Copy, Check, Trash2, Download,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const token = () => localStorage.getItem("ae_token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${token()}` } });


function Score({ label, value, tone = "neutral" }) {
  const bg = tone === "good" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
            : tone === "bad" ? "bg-rose-50 border-rose-200 text-rose-800"
            : "bg-indigo-50 border-indigo-200 text-indigo-800";
  return (
    <div className={`border ${bg} px-3 py-2`} data-testid={`si-score-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-serif font-semibold">{value}</div>
    </div>
  );
}


function Hit({ hit, onDeepDive, onCite, cited }) {
  return (
    <div className="group relative border border-zinc-200 hover:border-indigo-300 bg-white transition-colors" data-testid="si-hit">
      <a
        href={hit.url}
        target="_blank"
        rel="noreferrer noopener"
        className="flex items-start gap-2 px-3 py-2"
      >
        <ExternalLink size={14} className="mt-1 text-zinc-400 group-hover:text-indigo-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-900 group-hover:text-indigo-700 truncate">
            {hit.title || hit.url}
          </div>
          {hit.snippet && (
            <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{hit.snippet}</div>
          )}
          <div className="text-[10px] text-zinc-400 mt-1 flex items-center gap-2">
            <span className="uppercase tracking-wider">{hit.provider}</span>
            {hit.published_date && <span>· {hit.published_date.slice(0, 10)}</span>}
            {hit.author && <span>· {hit.author.length > 30 ? hit.author.slice(0, 30) + "…" : hit.author}</span>}
          </div>
        </div>
      </a>
      {/* Action row — deep-dive (Brave) or cite (Exa) */}
      {(onDeepDive || onCite) && (
        <div className="flex gap-1 border-t border-zinc-100 px-2 py-1 bg-zinc-50">
          {onDeepDive && (
            <button onClick={() => onDeepDive(hit)}
              data-testid="si-hit-deepdive"
              className="text-[11px] px-2 py-0.5 text-indigo-700 hover:bg-indigo-100 transition-colors flex items-center gap-1">
              <Search size={11} /> Compare
            </button>
          )}
          {onCite && (
            <button onClick={() => onCite(hit)}
              disabled={!!cited}
              data-testid="si-hit-cite"
              className={`text-[11px] px-2 py-0.5 transition-colors flex items-center gap-1 ml-auto ${
                cited ? "text-emerald-700 cursor-default"
                      : "text-amber-800 hover:bg-amber-100"
              }`}>
              {cited ? (<><Check size={11} /> Added to bibliography</>)
                     : (<><BookOpen size={11} /> Add to bibliography</>)}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function Section({ icon: Icon, title, children, testId }) {
  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zinc-100">
        <Icon size={16} className="text-indigo-600" />
        <h4 className="font-serif text-lg text-zinc-900">{title}</h4>
      </div>
      {children}
    </div>
  );
}


export default function SubmissionIntelligencePanel({ analysisId, autoLoad = true }) {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  // Deep-dive modal: which hit + its paragraph_text
  const [deepDive, setDeepDive] = useState(null); // { hit, paragraph_text, query }
  // Bibliography
  const [citations, setCitations] = useState([]);
  const [citeStyle, setCiteStyle] = useState("harvard");
  const [citing, setCiting] = useState({});

  useEffect(() => {
    if (!analysisId || !autoLoad) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/submission-intelligence/${analysisId}`, authHeaders())
        .then((r) => { if (r.data.exists) setReport(r.data.report); })
        .catch(() => {}),
      axios.get(`${API}/analyses/${analysisId}/citations`, authHeaders())
        .then((r) => setCitations(r.data.citations || []))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [analysisId, autoLoad]);

  const citedUrls = new Set(citations.map((c) => c.url));

  const openDeepDive = (hit, paragraph_text, query) => {
    setDeepDive({ hit, paragraph_text: paragraph_text || "", query: query || "" });
  };

  const addCitation = async (hit) => {
    const key = `${hit.url}|${citeStyle}`;
    if (citing[key] || citedUrls.has(hit.url)) return;
    setCiting({ ...citing, [key]: true });
    try {
      const r = await axios.post(
        `${API}/analyses/${analysisId}/citations`,
        { style: citeStyle, hit },
        authHeaders(),
      );
      if (r.data.already_added) {
        toast.info("Already in your bibliography.");
      } else {
        setCitations((prev) => [...prev, r.data.citation]);
        toast.success("Added to your bibliography.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add citation.");
    } finally {
      setCiting((c) => { const n = { ...c }; delete n[key]; return n; });
    }
  };

  const removeCitation = async (id) => {
    try {
      await axios.delete(`${API}/analyses/${analysisId}/citations/${encodeURIComponent(id)}`, authHeaders());
      setCitations((prev) => prev.filter((c) => c.id !== id));
    } catch { toast.error("Could not remove citation."); }
  };

  const copyBibliography = async () => {
    const text = citations.map((c) => c.text).join("\n\n");
    try { await navigator.clipboard.writeText(text); toast.success("Bibliography copied."); }
    catch { toast.error("Could not copy — please select the text manually."); }
  };

  const downloadBibliography = async () => {
    if (!analysisId) return;
    try {
      const r = await axios.get(
        `${API}/analyses/${analysisId}/citations/export.docx`,
        { ...authHeaders(), responseType: "blob" },
      );
      // Extract filename from Content-Disposition, fall back to a default.
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : "references.docx";
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("References downloaded.");
    } catch { toast.error("Could not generate .docx. Please try again."); }
  };

  const run = async () => {
    if (!analysisId) return;
    setRunning(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/submission-intelligence/${analysisId}/run`, {}, authHeaders(),
      );
      setReport(r.data);
      toast.success("Submission intelligence ready.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "upgrade_required") {
        setError({ upgrade: true, message: detail.message });
      } else {
        setError({ message: typeof detail === "string" ? detail : "Could not run the check. Please try again." });
      }
    } finally {
      setRunning(false);
    }
  };

  // Loading initial cached report
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 py-4" data-testid="si-loading">
        <Loader2 size={16} className="animate-spin" /> Loading Submission Intelligence…
      </div>
    );
  }

  // The BIG button — before the student has run it
  if (!report) {
    return (
      <div className="relative overflow-hidden border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-amber-50 p-6" data-testid="si-cta-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-amber-500 flex items-center justify-center text-white shrink-0">
            <Search size={18} />
          </div>
          <div>
            <p className="text-sm text-indigo-700 uppercase tracking-widest font-semibold">Research &amp; Check Submission</p>
            <h3 className="font-serif text-2xl text-zinc-900 mt-1">One check. Three angles.</h3>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl">
              We&apos;ll scan your submission for public-web overlap, missing citations, and outdated references — then combine everything into a single Submission Intelligence Report with actionable improvements.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          <div className="border border-indigo-100 bg-white/60 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-900"><Globe size={12} /> Web Similarity</div>
            <div className="text-xs text-zinc-600 mt-0.5">Public content overlap</div>
          </div>
          <div className="border border-indigo-100 bg-white/60 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-900"><BookOpen size={12} /> Evidence</div>
            <div className="text-xs text-zinc-600 mt-0.5">Sources for uncited claims</div>
          </div>
          <div className="border border-indigo-100 bg-white/60 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-900"><ShieldCheck size={12} /> Risk</div>
            <div className="text-xs text-zinc-600 mt-0.5">Ready-to-submit rating</div>
          </div>
        </div>
        {error?.upgrade ? (
          <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900 mb-3" data-testid="si-upgrade-msg">
            {error.message} <a href="/pricing" className="underline font-semibold">Upgrade to Pro</a>.
          </div>
        ) : error?.message ? (
          <div className="bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-900 mb-3" data-testid="si-error">
            {error.message}
          </div>
        ) : null}
        <button
          onClick={run}
          disabled={running}
          data-testid="si-run-btn"
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {running ? (<><Loader2 size={15} className="animate-spin" /> Analysing your submission…</>)
                   : (<><Search size={15} /> Research &amp; Check Submission</>)}
        </button>
        {running && (
          <p className="text-xs text-zinc-500 mt-2">This takes about 20-40 seconds — we&apos;re running web similarity and academic evidence checks in parallel.</p>
        )}
      </div>
    );
  }

  // ---- REPORT VIEW ----
  const risk = report.submission_risk || {};
  const riskTone = risk.level === "low" ? "good" : (risk.level === "high" ? "bad" : "neutral");
  const evi = report.evidence_strength || {};
  const web = report.web_similarity || {};

  return (
    <div className="space-y-4" data-testid="si-report">
      {/* Header row — scores */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="si-scores">
        <Score label="Web Similarity" value={`${web.score ?? 0}%`}
          tone={web.score >= 60 ? "bad" : web.score >= 30 ? "neutral" : "good"} />
        <Score label="Evidence" value={`${evi.score ?? 0}%`}
          tone={evi.score >= 75 ? "good" : evi.score >= 50 ? "neutral" : "bad"} />
        <Score label="Risk" value={risk.level ? risk.level[0].toUpperCase() + risk.level.slice(1) : "–"} tone={riskTone} />
        <Score label="Skipped" value={report.router_summary?.skipped ?? 0} tone="neutral" />
      </div>

      {/* Submission risk banner */}
      <div className={`border px-4 py-3 flex items-center gap-3 ${
        risk.level === "high" ? "border-rose-200 bg-rose-50 text-rose-900"
        : risk.level === "medium" ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-emerald-200 bg-emerald-50 text-emerald-900"
      }`} data-testid="si-risk-banner">
        <AlertTriangle size={18} />
        <div className="flex-1">
          <div className="text-sm font-semibold">{risk.summary || "Submission checked."}</div>
          <div className="text-xs opacity-80 mt-0.5">
            {report.router_summary?.brave_queries || 0} web checks · {report.router_summary?.exa_queries || 0} evidence checks
          </div>
        </div>
        <button onClick={run} disabled={running} data-testid="si-rerun-btn"
          className="text-xs border border-current px-3 py-1 hover:bg-white/40">
          {running ? "Re-running…" : "Re-run"}
        </button>
      </div>

      {/* Actionable improvements — top of the fold */}
      <Section icon={Lightbulb} title="Actionable improvements" testId="si-section-improvements">
        <ul className="space-y-2">
          {(report.actionable_improvements || []).map((tip, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-800" data-testid="si-improvement">
              <span className="text-indigo-600 mt-0.5">•</span>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Web similarity */}
      {(web.matches || []).length > 0 && (
        <Section icon={Globe} title="Web similarity" testId="si-section-web">
          <p className="text-xs text-zinc-500 mb-3">
            Public sources that match phrases in your submission. Paraphrase or cite to reduce overlap.
          </p>
          <div className="space-y-3">
            {web.matches.map((m, mi) => (
              <div key={mi} className="border-l-2 border-indigo-200 pl-3" data-testid={`si-web-match-${mi}`}>
                <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">
                  Paragraph {m.paragraph_idx + 1} · {m.reason.replace(/_/g, " ")}
                </div>
                <div className="text-xs text-zinc-700 mb-2 italic">&ldquo;{m.query}&rdquo;</div>
                <div className="space-y-1.5">
                  {(m.hits || []).slice(0, 3).map((h, hi) => (
                    <Hit key={hi} hit={h}
                      onDeepDive={(hit) => openDeepDive(hit, m.paragraph_text, m.query)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Evidence strength */}
      {(evi.unsupported_claims || []).length > 0 && (
        <Section icon={BookOpen} title="Evidence strength" testId="si-section-evidence">
          <p className="text-xs text-zinc-500 mb-3">
            Claims we couldn&apos;t find a citation for — plus academic sources you can add to strengthen your argument.
          </p>
          <div className="space-y-3">
            {evi.unsupported_claims.map((c, ci) => (
              <div key={ci} className="border-l-2 border-amber-300 pl-3" data-testid={`si-evidence-claim-${ci}`}>
                <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">
                  Paragraph {c.paragraph_idx + 1} · {c.reason.replace(/_/g, " ")}
                </div>
                <div className="text-xs text-zinc-700 mb-2 italic">&ldquo;{c.query}&rdquo;</div>
                <div className="space-y-1.5">
                  {(c.hits || []).slice(0, 3).map((h, hi) => (
                    <Hit key={hi} hit={h}
                      onCite={addCitation}
                      cited={citedUrls.has(h.url)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Suggested evidence (dedup pool) */}
      {(report.suggested_evidence || []).length > 0 && (
        <Section icon={TrendingUp} title="Additional suggested evidence" testId="si-section-suggested">
          <p className="text-xs text-zinc-500 mb-3">
            Extra reputable sources that align with your topic — use these to broaden your bibliography.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {report.suggested_evidence.slice(0, 8).map((h, i) => (
              <Hit key={i} hit={h}
                onCite={addCitation}
                cited={citedUrls.has(h.url)} />
            ))}
          </div>
        </Section>
      )}

      {/* Outdated references */}
      {(report.outdated_references || []).length > 0 && (
        <Section icon={Calendar} title="Outdated references" testId="si-section-outdated">
          <p className="text-xs text-zinc-500 mb-3">
            References older than 10 years — check whether a newer study is now available.
          </p>
          <ul className="space-y-1.5">
            {report.outdated_references.map((r, i) => (
              <li key={i} className="text-sm flex items-start gap-2" data-testid="si-outdated-item">
                <Calendar size={13} className="text-amber-600 mt-1 shrink-0" />
                <div>
                  <span className="font-mono text-zinc-900">{r.citation}</span>
                  <span className="text-xs text-zinc-500 ml-2">· {r.age_years} years old</span>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Bibliography — cumulative saved citations from Cite-Generator */}
      {citations.length > 0 && (
        <Section icon={BookOpen} title={`Your bibliography (${citations.length})`} testId="si-section-bibliography">
          <div className="flex items-center gap-2 mb-3">
            <div className="inline-flex border border-zinc-200" role="radiogroup" aria-label="Citation style">
              <button
                onClick={() => setCiteStyle("harvard")}
                data-testid="si-cite-style-harvard"
                aria-pressed={citeStyle === "harvard"}
                className={`px-3 py-1 text-xs ${citeStyle === "harvard" ? "bg-indigo-600 text-white" : "bg-white text-zinc-600 hover:text-zinc-900"}`}>
                Harvard
              </button>
              <button
                onClick={() => setCiteStyle("apa")}
                data-testid="si-cite-style-apa"
                aria-pressed={citeStyle === "apa"}
                className={`px-3 py-1 text-xs ${citeStyle === "apa" ? "bg-indigo-600 text-white" : "bg-white text-zinc-600 hover:text-zinc-900"}`}>
                APA
              </button>
            </div>
            <div className="text-[11px] text-zinc-500 flex-1">New saves use this style. Existing entries keep the style they were saved in.</div>
            <button onClick={copyBibliography}
              data-testid="si-bib-copy"
              className="text-xs px-2.5 py-1 border border-zinc-300 hover:bg-zinc-50 flex items-center gap-1">
              <Copy size={12} /> Copy all
            </button>
            <button onClick={downloadBibliography}
              data-testid="si-bib-download-docx"
              className="text-xs px-2.5 py-1 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 flex items-center gap-1">
              <Download size={12} /> Download .docx
            </button>
          </div>
          <ol className="space-y-2 text-sm text-zinc-800" data-testid="si-bib-list">
            {citations.map((c) => (
              <li key={c.id} className="group flex items-start gap-2 border-l-2 border-indigo-200 pl-3 py-1">
                <span className="flex-1 leading-snug">
                  {c.text}
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-400">{c.style}</span>
                </span>
                <button onClick={() => removeCitation(c.id)}
                  data-testid={`si-bib-remove`}
                  title="Remove"
                  className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-rose-600 transition-opacity">
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* AI Provenance — informational Coming Soon card (admin-only detection today) */}
      <div className="mt-4 relative overflow-hidden border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-white p-5" data-testid="ai-provenance-coming-soon">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-white border border-indigo-200 flex items-center justify-center shrink-0 text-lg" aria-hidden>🧬</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-zinc-900">AI Provenance — Coming Soon</h4>
              <span className="text-[10px] uppercase tracking-widest font-bold bg-indigo-600 text-white px-1.5 py-0.5">Preview</span>
            </div>
            <p className="mt-1.5 text-[13px] text-zinc-600 leading-relaxed">
              MyAE is developing new ways to help you understand whether AI tools may have been
              involved in your writing. This feature will provide additional AI provenance
              signals alongside MyAE&apos;s existing Submission Intelligence checks.
            </p>
            <p className="mt-2 text-[11px] text-zinc-400">
              Detection will only be enabled once an official mechanism is available, integrated
              and validated. This card is informational; no detection is currently active.
            </p>
          </div>
        </div>
      </div>

      <div className="text-[10px] text-zinc-400 text-center py-2">
        Powered by Brave Search + Exa AI · Generated {report.generated_at ? new Date(report.generated_at).toLocaleString() : ""}
      </div>

      {/* -------- Similarity Deep-Dive Modal -------- */}
      {deepDive && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setDeepDive(null)}
          data-testid="si-deepdive-modal"
        >
          <div
            className="bg-white w-full max-w-5xl my-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <Search size={16} className="text-indigo-600" />
                <p className="font-serif text-lg text-zinc-900">Similarity deep-dive</p>
              </div>
              <button
                onClick={() => setDeepDive(null)}
                data-testid="si-deepdive-close"
                className="text-zinc-400 hover:text-zinc-900"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-0 divide-x divide-zinc-200">
              {/* LEFT — student's paragraph */}
              <div className="p-5">
                <p className="overline text-indigo-700 mb-2">Your paragraph</p>
                <div
                  className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap"
                  data-testid="si-deepdive-paragraph"
                  dangerouslySetInnerHTML={{
                    __html: (deepDive.paragraph_text || "")
                      .replace(/[&<>]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[m])
                      .replace(
                        new RegExp(
                          `(${deepDive.query
                            .split(/\s+/)
                            .filter((w) => w.length > 3)
                            .slice(0, 8)
                            .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
                            .join("|")})`,
                          "gi",
                        ),
                        '<mark class="bg-amber-200/70 text-zinc-900 px-0.5">$1</mark>',
                      ),
                  }}
                />
                <div className="mt-4 border-t border-zinc-100 pt-3">
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">Flagged phrase</p>
                  <p className="text-xs text-zinc-700 italic">&ldquo;{deepDive.query}&rdquo;</p>
                </div>
              </div>

              {/* RIGHT — matching source */}
              <div className="p-5 bg-zinc-50">
                <p className="overline text-amber-700 mb-2">Matching public source</p>
                <a
                  href={deepDive.hit.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="block text-sm font-semibold text-indigo-700 hover:underline mb-1"
                  data-testid="si-deepdive-source-link"
                >
                  {deepDive.hit.title || deepDive.hit.url}
                </a>
                <p className="text-[10px] text-zinc-400 mb-3">
                  {deepDive.hit.provider?.toUpperCase()}
                  {deepDive.hit.published_date && <> · {deepDive.hit.published_date.slice(0, 10)}</>}
                </p>
                {deepDive.hit.snippet && (
                  <div className="text-sm text-zinc-700 leading-relaxed bg-white border border-zinc-200 p-3 mb-3">
                    {deepDive.hit.snippet}
                  </div>
                )}
                <div className="border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <p className="font-semibold mb-1 flex items-center gap-1"><Lightbulb size={12} /> How to paraphrase</p>
                  <ol className="space-y-1 list-decimal list-inside">
                    <li>Read the source, then close the tab.</li>
                    <li>Rewrite the point in your own voice from memory.</li>
                    <li>Add a citation to the source below your paragraph.</li>
                  </ol>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200 bg-zinc-50">
              <button
                onClick={() => setDeepDive(null)}
                data-testid="si-deepdive-done"
                className="text-sm px-4 py-1.5 border border-zinc-300 hover:bg-white"
              >
                Close
              </button>
              <a
                href={deepDive.hit.url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-sm px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1"
                data-testid="si-deepdive-open"
              >
                <ExternalLink size={13} /> Open source
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
